import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import LanguageSelector from '../components/LanguageSelector';
import { Briefcase, CheckCircle, Clock, Users } from 'lucide-react';

const CraigslistLandingPage = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Get position from URL parameter if available
  const position = searchParams.get('position') || '';

  const handleApplyNow = () => {
    navigate('/apply', { state: { job: { title: position } } });
  };

  const benefits = [
    { icon: Clock, textKey: 'clBenefit1' },
    { icon: Users, textKey: 'clBenefit2' },
    { icon: CheckCircle, textKey: 'clBenefit3' },
    { icon: CheckCircle, textKey: 'clBenefit4' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Header */}
      <header className="bg-white shadow-md">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <Briefcase className="w-8 h-8 text-blue-600" />
            <h1 className="text-2xl font-bold text-slate-900" data-testid="cl-site-title">
              {t('siteTitle')}
            </h1>
          </div>
          <LanguageSelector />
        </div>
      </header>

      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div 
          className="h-[600px] relative bg-slate-900"
        >
          <img
            src="https://customer-assets.emergentagent.com/job_6242444f-4893-42d3-bda2-3c6d69220535/artifacts/b85rq5ux_ChatGPT%20Image%20Nov%2029%2C%202025%2C%2004_14_37%20PM.png"
            alt="Professional cleaning"
            className="absolute inset-0 w-full h-full object-cover object-[center_20%]"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 to-black/40" />
          
          <div className="relative h-full flex items-center justify-center px-4">
            <div className="max-w-3xl text-center text-white">
              <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6" data-testid="cl-hero-title">
                {t('clHeroTitle')}
              </h2>
              <p className="text-xl sm:text-2xl text-white/90 mb-8" data-testid="cl-hero-subtitle">
                {t('clHeroSubtitle')}
              </p>
              
              <button
                onClick={handleApplyNow}
                className="bg-blue-600 hover:bg-blue-700 text-white font-semibold text-lg px-12 py-4 rounded-lg shadow-2xl hover:shadow-blue-500/50 transition-all transform hover:scale-105"
                data-testid="cl-apply-now-button"
              >
                {t('clApplyNow')}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Benefits Section */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="bg-white/70 backdrop-blur-xl border border-white/20 shadow-xl rounded-2xl p-8">
          <h3 className="text-3xl font-bold text-slate-900 text-center mb-12" data-testid="cl-benefits-title">
            {t('clWhyJoin')}
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <div 
                  key={index}
                  className="flex items-start gap-4 p-6 bg-white rounded-xl border border-slate-100 hover:border-blue-200 transition-colors"
                  data-testid={`cl-benefit-${index}`}
                >
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Icon className="w-6 h-6 text-blue-600" />
                    </div>
                  </div>
                  <div>
                    <p className="text-slate-700 font-medium">
                      {t(benefit.textKey)}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl shadow-2xl p-12 text-center">
          <h3 className="text-3xl font-bold text-white mb-4" data-testid="cl-cta-title">
            {t('clReadyToStart')}
          </h3>
          <p className="text-xl text-white/90 mb-8">
            {t('clCtaDescription')}
          </p>
          <button
            onClick={handleApplyNow}
            className="bg-white text-blue-600 hover:bg-blue-50 font-semibold text-lg px-12 py-4 rounded-lg shadow-xl hover:shadow-2xl transition-all transform hover:scale-105"
            data-testid="cl-apply-now-button-bottom"
          >
            {t('clApplyNow')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CraigslistLandingPage;